import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import { useNavigate } from 'react-router-dom'
import { adminService, type EventSummary } from '../../services/adminService'
import { LoadingState, EmptyState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'

/**
 * Template library: system "starter" blueprints plus any user-saved templates.
 * "Use template" instantiates a new draft event (with placeholder dates the
 * admin then sets); user-saved templates can be deleted (starters cannot).
 */
export default function AdminTemplatesPage() {
  const [templates, setTemplates] = useState<EventSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)
  const navigate = useNavigate()
  const { notify } = useToast()

  const load = () =>
    adminService
      .listTemplates()
      .then(setTemplates)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const use = async (t: EventSummary) => {
    setBusyId(t.id)
    try {
      const created = await adminService.createFromTemplate(t.id)
      notify('Event created from template — set your dates')
      navigate(`/admin/events/${created.id}/edit`)
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setBusyId(null)
    }
  }

  const remove = async (t: EventSummary) => {
    const label = t.templateName || t.name
    if (!(await confirmDelete(`Delete template "${label}"?`))) return
    try {
      await adminService.deleteTemplate(t.id)
      notify('Template deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate('/admin')}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        All events
      </Button>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Templates
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Start a new event from a ready-made blueprint
        </Typography>
      </Box>

      {loading ? (
        <LoadingState />
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<DashboardCustomizeRoundedIcon fontSize="large" />}
          title="No templates yet"
          description="Save an event as a template to reuse it here."
        />
      ) : (
        <Stack spacing={1.5}>
          {templates.map((t) => (
            <Card
              key={t.id}
              variant="outlined"
              sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {t.templateName || t.name}
                  </Typography>
                  {t.isStarter && (
                    <Chip
                      label="Starter"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: '#0075BC',
                        bgcolor: 'rgba(0,117,188,0.1)',
                      }}
                    />
                  )}
                </Stack>
                {t.templateDescription && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {t.templateDescription}
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                onClick={() => use(t)}
                disabled={busyId !== null}
              >
                {busyId === t.id ? 'Creating…' : 'Use template'}
              </Button>
              {!t.isStarter && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => remove(t)}
                  aria-label="Delete template"
                >
                  <DeleteOutlineRoundedIcon />
                </IconButton>
              )}
            </Card>
          ))}
        </Stack>
      )}
    </Container>
  )
}
